/**
 * Body Map SVG Components - SuiOS
 * Interactive garment diagrams for alteration recording
 */

import { cn } from '@/lib/utils';

// Zone configurations for each garment type
export const GARMENT_ZONES = {
  jacket: {
    front: [
      { id: 'collar', label: 'Collar/Lapels', path: 'M 140,45 L 160,25 L 200,25 L 240,25 L 260,45 L 240,65 L 200,55 L 160,65 Z' },
      { id: 'shoulders_left', label: 'Left Shoulder', path: 'M 100,45 L 140,45 L 160,65 L 160,85 L 100,85 Z' },
      { id: 'shoulders_right', label: 'Right Shoulder', path: 'M 260,45 L 300,45 L 300,85 L 240,85 L 240,65 Z' },
      { id: 'chest_left', label: 'Left Chest', path: 'M 120,85 L 160,85 L 170,150 L 120,150 Z' },
      { id: 'chest_right', label: 'Right Chest', path: 'M 240,85 L 280,85 L 280,150 L 230,150 Z' },
      { id: 'waist_left', label: 'Left Waist', path: 'M 120,150 L 170,150 L 175,220 L 115,220 Z' },
      { id: 'waist_right', label: 'Right Waist', path: 'M 230,150 L 280,150 L 285,220 L 225,220 Z' },
      { id: 'sleeve_left_upper', label: 'Left Sleeve (Upper)', path: 'M 50,85 L 100,85 L 100,160 L 45,160 Z' },
      { id: 'sleeve_left_lower', label: 'Left Sleeve (Lower)', path: 'M 45,160 L 100,160 L 95,250 L 40,250 Z' },
      { id: 'sleeve_right_upper', label: 'Right Sleeve (Upper)', path: 'M 300,85 L 350,85 L 355,160 L 300,160 Z' },
      { id: 'sleeve_right_lower', label: 'Right Sleeve (Lower)', path: 'M 300,160 L 355,160 L 360,250 L 305,250 Z' },
      { id: 'jacket_hem', label: 'Hem', path: 'M 115,220 L 285,220 L 290,260 L 110,260 Z' },
      { id: 'front_center', label: 'Front Center', path: 'M 170,85 L 230,85 L 225,260 L 175,260 Z' },
    ],
    back: [
      { id: 'back_shoulders', label: 'Back Shoulders', path: 'M 100,45 L 300,45 L 300,100 L 100,100 Z' },
      { id: 'back_upper', label: 'Upper Back', path: 'M 120,100 L 280,100 L 280,170 L 120,170 Z' },
      { id: 'back_lower', label: 'Lower Back', path: 'M 115,170 L 285,170 L 290,260 L 110,260 Z' },
      { id: 'vent', label: 'Vent', path: 'M 185,220 L 215,220 L 215,260 L 185,260 Z' },
    ],
  },
  trousers: {
    front: [
      { id: 'waistband', label: 'Waistband', path: 'M 100,30 L 300,30 L 295,60 L 105,60 Z' },
      { id: 'rise', label: 'Rise/Crotch', path: 'M 160,60 L 240,60 L 240,130 L 200,160 L 160,130 Z' },
      { id: 'seat_hips', label: 'Seat/Hips', path: 'M 105,60 L 160,60 L 160,130 L 100,130 Z M 240,60 L 295,60 L 300,130 L 240,130 Z' },
      { id: 'thigh_left', label: 'Left Thigh', path: 'M 100,130 L 160,130 L 155,220 L 95,220 Z' },
      { id: 'thigh_right', label: 'Right Thigh', path: 'M 240,130 L 300,130 L 305,220 L 245,220 Z' },
      { id: 'knee_left', label: 'Left Knee', path: 'M 95,220 L 155,220 L 150,290 L 90,290 Z' },
      { id: 'knee_right', label: 'Right Knee', path: 'M 245,220 L 305,220 L 310,290 L 250,290 Z' },
      { id: 'calf_left', label: 'Left Calf', path: 'M 90,290 L 150,290 L 145,370 L 85,370 Z' },
      { id: 'calf_right', label: 'Right Calf', path: 'M 250,290 L 310,290 L 315,370 L 255,370 Z' },
      { id: 'hem_left', label: 'Left Hem', path: 'M 85,370 L 145,370 L 140,400 L 80,400 Z' },
      { id: 'hem_right', label: 'Right Hem', path: 'M 255,370 L 315,370 L 320,400 L 260,400 Z' },
    ],
  },
  shirt: {
    front: [
      { id: 'collar', label: 'Collar', path: 'M 150,20 L 200,10 L 250,20 L 240,50 L 200,40 L 160,50 Z' },
      { id: 'shoulder_left', label: 'Left Shoulder', path: 'M 100,50 L 160,50 L 160,80 L 100,80 Z' },
      { id: 'shoulder_right', label: 'Right Shoulder', path: 'M 240,50 L 300,50 L 300,80 L 240,80 Z' },
      { id: 'chest', label: 'Chest', path: 'M 130,80 L 270,80 L 275,160 L 125,160 Z' },
      { id: 'waist', label: 'Waist', path: 'M 125,160 L 275,160 L 280,240 L 120,240 Z' },
      { id: 'shirt_length', label: 'Shirt Length', path: 'M 120,240 L 280,240 L 285,280 L 115,280 Z' },
      { id: 'sleeve_left', label: 'Left Sleeve', path: 'M 40,80 L 100,80 L 100,180 L 35,180 Z' },
      { id: 'sleeve_right', label: 'Right Sleeve', path: 'M 300,80 L 360,80 L 365,180 L 300,180 Z' },
      { id: 'cuff_left', label: 'Left Cuff', path: 'M 35,180 L 100,180 L 95,210 L 30,210 Z' },
      { id: 'cuff_right', label: 'Right Cuff', path: 'M 300,180 L 365,180 L 370,210 L 305,210 Z' },
    ],
  },
  sherwani: {
    front: [
      { id: 'collar', label: 'Collar', path: 'M 160,30 L 200,20 L 240,30 L 230,60 L 200,50 L 170,60 Z' },
      { id: 'shoulders', label: 'Shoulders', path: 'M 100,50 L 300,50 L 300,90 L 100,90 Z' },
      { id: 'chest', label: 'Chest', path: 'M 120,90 L 280,90 L 285,170 L 115,170 Z' },
      { id: 'waist', label: 'Waist', path: 'M 115,170 L 285,170 L 290,250 L 110,250 Z' },
      { id: 'lower_body', label: 'Lower Body', path: 'M 110,250 L 290,250 L 295,380 L 105,380 Z' },
      { id: 'hem', label: 'Hem', path: 'M 105,380 L 295,380 L 300,420 L 100,420 Z' },
      { id: 'sleeve_left', label: 'Left Sleeve', path: 'M 40,90 L 100,90 L 95,220 L 35,220 Z' },
      { id: 'sleeve_right', label: 'Right Sleeve', path: 'M 300,90 L 360,90 L 365,220 L 305,220 Z' },
    ],
  },
  bandhgala: {
    front: [
      { id: 'collar', label: 'Mandarin Collar', path: 'M 160,25 L 200,15 L 240,25 L 235,55 L 200,45 L 165,55 Z' },
      { id: 'shoulders', label: 'Shoulders', path: 'M 100,45 L 300,45 L 300,85 L 100,85 Z' },
      { id: 'chest', label: 'Chest', path: 'M 120,85 L 280,85 L 280,150 L 120,150 Z' },
      { id: 'waist', label: 'Waist', path: 'M 115,150 L 285,150 L 285,220 L 115,220 Z' },
      { id: 'hem', label: 'Hem', path: 'M 115,220 L 285,220 L 290,260 L 110,260 Z' },
      { id: 'sleeve_left', label: 'Left Sleeve', path: 'M 40,85 L 100,85 L 95,200 L 35,200 Z' },
      { id: 'sleeve_right', label: 'Right Sleeve', path: 'M 300,85 L 360,85 L 365,200 L 305,200 Z' },
    ],
  },
  nehru_vest: {
    front: [
      { id: 'collar', label: 'Collar', path: 'M 160,25 L 200,15 L 240,25 L 235,50 L 200,42 L 165,50 Z' },
      { id: 'shoulders', label: 'Shoulders', path: 'M 120,45 L 280,45 L 280,80 L 120,80 Z' },
      { id: 'chest', label: 'Chest', path: 'M 130,80 L 270,80 L 270,150 L 130,150 Z' },
      { id: 'waist', label: 'Waist', path: 'M 125,150 L 275,150 L 280,220 L 120,220 Z' },
      { id: 'hem', label: 'Hem', path: 'M 120,220 L 280,220 L 285,250 L 115,250 Z' },
    ],
  },
  kurta: {
    front: [
      { id: 'collar', label: 'Collar', path: 'M 160,25 L 200,15 L 240,25 L 235,55 L 200,45 L 165,55 Z' },
      { id: 'shoulders', label: 'Shoulders', path: 'M 100,45 L 300,45 L 300,85 L 100,85 Z' },
      { id: 'chest', label: 'Chest', path: 'M 120,85 L 280,85 L 280,160 L 120,160 Z' },
      { id: 'waist', label: 'Waist', path: 'M 115,160 L 285,160 L 290,250 L 110,250 Z' },
      { id: 'lower_body', label: 'Lower Body', path: 'M 110,250 L 290,250 L 295,350 L 105,350 Z' },
      { id: 'hem', label: 'Hem', path: 'M 105,350 L 295,350 L 300,380 L 100,380 Z' },
      { id: 'sleeve_left', label: 'Left Sleeve', path: 'M 40,85 L 100,85 L 95,200 L 35,200 Z' },
      { id: 'sleeve_right', label: 'Right Sleeve', path: 'M 300,85 L 360,85 L 365,200 L 305,200 Z' },
      { id: 'slit_left', label: 'Left Slit', path: 'M 110,280 L 130,280 L 130,380 L 110,380 Z' },
      { id: 'slit_right', label: 'Right Slit', path: 'M 270,280 L 290,280 L 290,380 L 270,380 Z' },
    ],
  },
  waistcoat: {
    front: [
      { id: 'collar_lapels', label: 'Collar/Lapels', path: 'M 140,30 L 200,20 L 260,30 L 250,70 L 200,55 L 150,70 Z' },
      { id: 'shoulders', label: 'Shoulders', path: 'M 120,50 L 280,50 L 280,85 L 120,85 Z' },
      { id: 'chest', label: 'Chest', path: 'M 130,85 L 270,85 L 270,150 L 130,150 Z' },
      { id: 'waist', label: 'Waist', path: 'M 125,150 L 275,150 L 280,220 L 120,220 Z' },
      { id: 'hem', label: 'Hem', path: 'M 120,220 L 280,220 L 285,260 L 115,260 Z' },
    ],
  },
  pyjama: {
    front: [
      { id: 'waistband', label: 'Waistband', path: 'M 100,30 L 300,30 L 295,70 L 105,70 Z' },
      { id: 'seat_hips', label: 'Seat/Hips', path: 'M 105,70 L 295,70 L 290,140 L 200,160 L 110,140 Z' },
      { id: 'thigh_left', label: 'Left Thigh', path: 'M 100,140 L 160,140 L 155,240 L 95,240 Z' },
      { id: 'thigh_right', label: 'Right Thigh', path: 'M 240,140 L 300,140 L 305,240 L 245,240 Z' },
      { id: 'leg_left', label: 'Left Leg', path: 'M 95,240 L 155,240 L 150,360 L 90,360 Z' },
      { id: 'leg_right', label: 'Right Leg', path: 'M 245,240 L 305,240 L 310,360 L 250,360 Z' },
      { id: 'hem_left', label: 'Left Hem', path: 'M 90,360 L 150,360 L 145,390 L 85,390 Z' },
      { id: 'hem_right', label: 'Right Hem', path: 'M 250,360 L 310,360 L 315,390 L 255,390 Z' },
    ],
  },
};

// Alteration types for each zone category
export const ALTERATION_TYPES = {
  default: ['shorten', 'lengthen', 'take_in', 'let_out', 'reshape', 'reposition'],
  hem: ['shorten', 'lengthen'],
  waist: ['take_in', 'let_out', 'reshape'],
  sleeve: ['shorten', 'lengthen', 'take_in', 'let_out'],
  collar: ['reshape', 'reposition', 'take_in', 'let_out'],
  shoulders: ['narrow', 'widen', 'reposition'],
  chest: ['take_in', 'let_out', 'reshape'],
  back: ['take_in', 'let_out', 'reshape'],
};

// Get alteration types for a specific zone
export function getAlterationTypesForZone(zoneId) {
  if (zoneId.includes('hem')) return ALTERATION_TYPES.hem;
  if (zoneId.includes('waist')) return ALTERATION_TYPES.waist;
  if (zoneId.includes('sleeve') || zoneId.includes('cuff')) return ALTERATION_TYPES.sleeve;
  if (zoneId.includes('collar') || zoneId.includes('lapel')) return ALTERATION_TYPES.collar;
  if (zoneId.includes('shoulder')) return ALTERATION_TYPES.shoulders;
  if (zoneId.includes('chest')) return ALTERATION_TYPES.chest;
  if (zoneId.includes('back')) return ALTERATION_TYPES.back;
  return ALTERATION_TYPES.default;
}

// SVG Zone Component
export function GarmentZone({
  zone,
  isSelected,
  isMarked,
  isCompleted,
  adjustment,
  onClick,
  disabled,
}) {
  const getZoneStyle = () => {
    if (isCompleted) return { fill: 'rgba(34, 197, 94, 0.4)', stroke: '#22C55E', strokeWidth: 2 };
    if (isMarked) return { fill: 'rgba(197, 165, 90, 0.5)', stroke: '#C5A55A', strokeWidth: 2 };
    if (isSelected) return { fill: 'rgba(197, 165, 90, 0.3)', stroke: '#C5A55A', strokeWidth: 2 };
    return { fill: 'rgba(27, 42, 74, 0.05)', stroke: '#1B2A4A', strokeWidth: 1, strokeOpacity: 0.3 };
  };

  const style = getZoneStyle();

  return (
    <g className={cn('cursor-pointer transition-all', disabled && 'opacity-50 pointer-events-none')}>
      <path
        d={zone.path}
        fill={style.fill}
        stroke={style.stroke}
        strokeWidth={style.strokeWidth}
        strokeOpacity={style.strokeOpacity || 1}
        onClick={() => onClick?.(zone)}
        className="hover:brightness-110 transition-all"
      />
      {isMarked && adjustment && (
        <text
          x={getZoneCentroid(zone.path).x}
          y={getZoneCentroid(zone.path).y}
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-xs font-bold pointer-events-none select-none"
          fill="#1B2A4A"
        >
          {adjustment > 0 ? '+' : ''}{adjustment}cm
        </text>
      )}
    </g>
  );
}

// Calculate centroid of an SVG path (approximate)
function getZoneCentroid(pathData) {
  // Parse path data to extract coordinates
  const coords = pathData.match(/[\d.]+/g);
  if (!coords || coords.length < 4) return { x: 200, y: 150 };
  
  let sumX = 0, sumY = 0, count = 0;
  for (let i = 0; i < coords.length - 1; i += 2) {
    sumX += parseFloat(coords[i]);
    sumY += parseFloat(coords[i + 1]);
    count++;
  }
  return { x: Math.round(sumX / count), y: Math.round(sumY / count) };
}

// Get SVG viewBox dimensions for garment type
export function getGarmentViewBox(garmentType) {
  switch (garmentType) {
    case 'trousers':
    case 'pyjama':
      return '0 0 400 420';
    case 'sherwani':
    case 'kurta':
      return '0 0 400 440';
    default:
      return '0 0 400 300';
  }
}

export default GARMENT_ZONES;
